import { useContext } from 'react';
import type { KeyboardShortcut } from 'components/editor/hooks/useKeyboardShortcuts';
import { useKeyboardShortcuts } from 'components/editor/hooks/useKeyboardShortcuts';
import { SelectionContext } from '../SelectedBlockInfoProvider';
import { useDraggableBlockManipulations } from './useDraggableBlockManipulations';
import { useNewBlockDeletedHandler } from 'components/editor/hooks/useNewBlockDeletedHandler.ts';
import { useSectionData } from '../../Sections/useSectionContext';
import { useBlockLockStatus } from 'components/editor/hooks/useBlockLockStatus';

export const useBlockKeyboardShortcuts = (blockId: string) => {
  const { selectedBlockIdByWrapper, selectedBlockInfoByWrapper } = useContext(SelectionContext);
  const { handleKeyboardMove } = useDraggableBlockManipulations();
  const gridBlockDeletedHandler = useNewBlockDeletedHandler();
  const deleteBlockHandler = gridBlockDeletedHandler;
  const { blocksContent } = useSectionData();
  const { isLocked: isBlockLocked } = useBlockLockStatus(blockId);
  const isBlockMoveableByKeyboard = Boolean(selectedBlockIdByWrapper) && !isBlockLocked;
  const isCurrentBlockSelected = selectedBlockIdByWrapper === blockId;

  const handleBlockDelete = async () => {
    const { blockId, sectionId } = selectedBlockInfoByWrapper;
    const blockContent = blockId && sectionId && blocksContent[blockId];

    if (!blockContent) return;

    deleteBlockHandler(sectionId, blockId);
  };

  const onKeyboardMove = ({ deltaX, deltaY }: { deltaX: number; deltaY: number }) => {
    const { blockId, sectionId } = selectedBlockInfoByWrapper;
    const blockContent = blockId && sectionId && blocksContent[blockId];

    if (!blockContent) return;

    handleKeyboardMove({ blockConfig: blockContent.blockConfig, deltaX, deltaY });
  };

  const keyboardShortcuts: KeyboardShortcut[] = [
    {
      key: 'Delete',
      callback: () => {
        handleBlockDelete();
      },
      isEnable: !isBlockLocked,
    },
    {
      key: 'Backspace',
      callback: () => {
        handleBlockDelete();
      },
      isEnable: !isBlockLocked,
    },
    {
      key: 'ArrowUp',
      shift: true,
      callback: () => {
        if (!selectedBlockIdByWrapper) {
          return;
        }
        onKeyboardMove({ deltaX: 0, deltaY: -10 });
      },
      isEnable: isBlockMoveableByKeyboard,
    },
    {
      key: 'ArrowDown',
      shift: true,
      callback: () => {
        if (!selectedBlockIdByWrapper) {
          return;
        }

        onKeyboardMove({ deltaX: 0, deltaY: 10 });
      },
      isEnable: isBlockMoveableByKeyboard,
    },
    {
      key: 'ArrowLeft',
      shift: true,
      callback: () => {
        if (!selectedBlockIdByWrapper) {
          return;
        }

        onKeyboardMove({ deltaX: -10, deltaY: 0 });
      },
      isEnable: isBlockMoveableByKeyboard,
    },
    {
      key: 'ArrowRight',
      shift: true,
      callback: () => {
        if (!selectedBlockIdByWrapper) {
          return;
        }

        onKeyboardMove({ deltaX: 10, deltaY: 0 });
      },
      isEnable: isBlockMoveableByKeyboard,
    },
    // Regular Arrow shortcuts (1-pixel movement) - must come after Shift+Arrow
    {
      key: 'ArrowUp',
      callback: () => {
        if (!selectedBlockIdByWrapper) {
          return;
        }
        onKeyboardMove({ deltaX: 0, deltaY: -1 });
      },
      isEnable: isBlockMoveableByKeyboard,
    },
    {
      key: 'ArrowDown',
      callback: () => {
        if (!selectedBlockIdByWrapper) {
          return;
        }

        onKeyboardMove({ deltaX: 0, deltaY: 1 });
      },
      isEnable: isBlockMoveableByKeyboard,
    },
    {
      key: 'ArrowLeft',
      callback: () => {
        if (!selectedBlockIdByWrapper) {
          return;
        }

        onKeyboardMove({ deltaX: -1, deltaY: 0 });
      },
      isEnable: isBlockMoveableByKeyboard,
    },
    {
      key: 'ArrowRight',
      callback: () => {
        if (!selectedBlockIdByWrapper) {
          return;
        }

        onKeyboardMove({ deltaX: 1, deltaY: 0 });
      },
      isEnable: isBlockMoveableByKeyboard,
    },
  ];

  useKeyboardShortcuts(isCurrentBlockSelected ? keyboardShortcuts : []);
};
